import React from 'react';
import { t } from '@citrite/translate';
import {
	CustomBadge,
	fromThemeColors,
	layout,
	palette,
	Tooltip,
	typography,
} from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import { useDesktopDetails } from 'App/Activity/useDesktopDetails';
import { useResourceState } from 'App/Activity/useResourceState';
import { DesktopIcon } from 'App/Tile/DesktopIcon';
import { FavoriteIconWithRef } from 'App/Tile/FavoriteIcon';
import { PowerStateBadge } from 'App/Tile/PowerStateBadge';
import { getDesktopWallpaper } from 'App/Tile/utils';
import { styles } from 'Components/ResourceTile/Favorite.styles';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

const OS_BADGE_MAX_WIDTH = '100px';

const StyledImageContainer = styled.div<{ wallpaper: string }>`
	position: relative;
	flex: 1;
	background-image: url(${props => props.wallpaper});
	border-radius: ${layout.smallSpace};
	background-size: cover;
	background-position: center;
`;

const HeaderWrapper = styled.div`
	display: flex;
	flex: 1;
	padding: ${layout.smallSpace} ${layout.smallSpace} 0;
`;

const TileStateOSWrapper = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	background: ${fromThemeColors('primaryContainerBackground')};
	padding: ${layout.tinySpace} ${layout.tinySpace} 0 0;
	border-radius: 0 ${layout.smallSpace};
	display: flex;
	flex-direction: row;
	align-items: center;
	overflow: hidden;
	gap: ${layout.tinySpace};
`;

const StyledCustomBadge = styled(CustomBadge)`
	display: block;
	height: ${layout.space22};
	font-size: ${typography.sizes.xsml2};
	color: ${palette.grey900};
	line-height: normal;
	border: 1px solid ${palette.grey300};
	padding: ${layout.tinySpace} ${layout.space6};
	background-color: ${palette.grey100};
	max-width: ${OS_BADGE_MAX_WIDTH};
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;

	.detailed-desktop-tile-container:hover & {
		text-decoration: underline dotted;
		text-underline-offset: 1px;
	}
`;

const StyledTooltip = styled(Tooltip)`
	display: flex;
`;

const TooltipContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${layout.smallSpace};
	text-align: left;
	font-size: ${typography.sizes.sml};
	color: ${palette.white};
`;

const TooltipRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: ${layout.tinySpace};
`;

const TooltipLabel = styled.span`
	font-weight: ${typography.weights.semiBold};
`;

const TooltipValue = styled.span`
	font-weight: ${typography.weights.regular};
`;

interface OSTooltipContentProps {
	osType?: string;
	osVersion?: string;
}

const OSBadgeTooltipContent: React.FC<OSTooltipContentProps> = ({
	osType,
	osVersion,
}) => (
	<TooltipContent>
		{osType && (
			<TooltipRow>
				<TooltipLabel>{t('Workspace:tooltip_os_type')}:</TooltipLabel>
				<TooltipValue>{osType}</TooltipValue>
			</TooltipRow>
		)}
		{osVersion && (
			<TooltipRow>
				<TooltipLabel>{t('Workspace:tooltip_os_version')}:</TooltipLabel>
				<TooltipValue>{osVersion}</TooltipValue>
			</TooltipRow>
		)}
	</TooltipContent>
);

interface DetailedDesktopTileHeaderProps {
	resource: Resource;
	favoriteRef: React.RefObject<HTMLInputElement>;
	canShowTroubleshootStatus: boolean;
}
export const DetailedDesktopTileHeader: React.FC<DetailedDesktopTileHeaderProps> = ({
	resource,
	favoriteRef,
	canShowTroubleshootStatus,
}) => {
	const { value: resourceContext } = useLoadableResourceContext();
	const hasSubscriptionsEnabled = resourceContext.subscriptionsEnabled();
	const {
		desktopWallpaper,
		desktopOS,
		osType,
		osVersion,
		isLoading: isMachineListLoading,
	} = useDesktopDetails(resource);
	const { state, lastUpdatedTime } = useResourceState(resource);

	const isLoading = state === PowerState.LOADING || isMachineListLoading;
	const shouldShowOSBadge = desktopOS && !isLoading;

	return (
		<HeaderWrapper>
			<StyledImageContainer wallpaper={getDesktopWallpaper(desktopWallpaper)}>
				{hasSubscriptionsEnabled && (
					<FavoriteIconWithRef
						resource={resource}
						ref={favoriteRef}
						styles={{ desktop: styles.detailedDesktop }}
					/>
				)}
				<DesktopIcon resource={resource} />
				<TileStateOSWrapper>
					<PowerStateBadge
						resource={resource}
						powerState={state}
						lastUpdatedTime={lastUpdatedTime}
						canShowTroubleshootStatus={canShowTroubleshootStatus}
					/>
					{shouldShowOSBadge && (
						<StyledTooltip
							hoverTrigger={<StyledCustomBadge>{desktopOS}</StyledCustomBadge>}
							className="tooltip"
						>
							<OSBadgeTooltipContent osType={osType} osVersion={osVersion} />
						</StyledTooltip>
					)}
				</TileStateOSWrapper>
			</StyledImageContainer>
		</HeaderWrapper>
	);
};
